import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import { apiKey } from "../../../config/config";
import { toast } from "react-toastify";
import { languageHelper } from "../../../config/helperFunctions";
import { FaSyncAlt } from "react-icons/fa";

function AddGiftCardForm() {
  const user = JSON.parse(localStorage.getItem("rms-admin"));
  const currency = user?.user?.currency || "SAR";
  const { id } = useParams();
  const navigate = useNavigate();

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  const [formData, setFormData] = useState({
    code: "",
    title: "",
    price: "",
    value: "",
    expiration_date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0],
    customer_id: null,
    status: "active",
  });

  // Helper to generate a random code
  const generateRandomCode = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    const segment = (len) =>
      Array.from({ length: len }, () =>
        chars.charAt(Math.floor(Math.random() * chars.length))
      ).join("");
    return `GC-${segment(4)}-${segment(4)}`;
  };

  // Generate initial code on add mode
  useEffect(() => {
    if (!id) {
      setFormData((prev) => (prev.code ? prev : { ...prev, code: generateRandomCode() }));
    }
  }, [id]);

  // Fetch customers for specific customer selection
  useEffect(() => {
    axios
      .get(`${apiKey}/api/rms/admin/customer`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      })
      .then((res) => {
        const custList = Array.isArray(res.data) ? res.data : [];
        const options = [
          { value: null, label: languageHelper("All Customers (Anyone Can Redeem)") },
          ...custList.map((c) => ({
            value: c.id,
            label: `${c.name} ${c.phone ? `(${c.phone})` : ""}`,
          })),
        ];
        setCustomers(options);
      })
      .catch((err) => {
        console.error("Fetch customers error:", err);
      })
      .finally(() => {
        if (!id) setPageLoading(false);
      });
  }, [user?.token, id]);

  // Fetch gift card details if editing
  useEffect(() => {
    if (!id) return;

    axios
      .get(`${apiKey}/api/rms/admin/gift-cards/${id}`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      })
      .then((res) => {
        if (res.data?.success && res.data?.data) {
          const card = res.data.data;
          setFormData({
            code: card.code,
            title: card.title || "",
            price: card.price,
            value: card.value,
            expiration_date: card.expiration_date
              ? card.expiration_date.split("T")[0]
              : "",
            customer_id: card.customer_id,
            status: card.status || "active",
          });
        }
      })
      .catch((err) => {
        console.error("Fetch gift card error:", err);
        toast.error("Failed to load gift card details", {
          position: "bottom-right",
          theme: "colored",
        });
      })
      .finally(() => {
        setPageLoading(false);
      });
  }, [id, user?.token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.code.trim()) {
      toast.warning("Please enter or generate a Gift Card code.");
      return;
    }

    if (!formData.value || parseFloat(formData.value) <= 0) {
      toast.warning("Please enter a valid Gift Card Value.");
      return;
    }

    if (!formData.expiration_date) {
      toast.warning("Please select an Expiration Date.");
      return;
    }

    setLoading(true);

    const payload = {
      code: formData.code.trim().toUpperCase(),
      title: formData.title || null,
      price: parseFloat(formData.price) || 0,
      value: parseFloat(formData.value),
      expiration_date: formData.expiration_date,
      customer_id: formData.customer_id || null,
      outlet_id: user?.user?.outlet_id || null,
      status: formData.status,
    };

    const request = id
      ? axios.put(`${apiKey}/api/rms/admin/gift-cards/${id}`, payload, {
          headers: { Authorization: `Bearer ${user?.token}` },
        })
      : axios.post(`${apiKey}/api/rms/admin/gift-cards`, payload, {
          headers: { Authorization: `Bearer ${user?.token}` },
        });

    request
      .then((res) => {
        toast.success(
          res.data?.message ||
            (id
              ? "Gift Card updated successfully!"
              : "Gift Card created successfully!"),
          {
            position: "bottom-right",
            theme: "colored",
          }
        );
        navigate(-1);
      })
      .catch((err) => {
        console.error("Submit error:", err);
        const errorMsg =
          err.response?.data?.message || "Failed to save gift card.";
        toast.error(errorMsg, {
          position: "bottom-right",
          theme: "colored",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const selectedCustomerOption =
    customers.find((opt) => opt.value === formData.customer_id) || customers[0];

  if (pageLoading) {
    return (
      <div className="absolute top-0 left-0 w-full h-full grid place-items-center">
        <img src="/assets/gif/dash2.gif" alt="" />
      </div>
    );
  }

  return (
    <main>
      <div className="relative my-16 flex flex-col min-w-0 break-words w-full mb-6 shadow-lg bg-white rounded-lg p-4 border-0">
        <div className="flex justify-between items-center mb-4">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-d-bg-text text-2xl">
              {languageHelper(id ? "Edit Gift Card" : "Add Gift Card")}
            </h1>
          </div>
        </div>

        <div className="w-full">
          <form className="w-full" onSubmit={handleSubmit}>
            <div className="grid grid-cols-4 gap-x-8 gap-y-2 w-full">
              {/* Card Code */}
              <label htmlFor="" className="col-span-2">
                <span className="text-d-bg-text text-sm mb-2 relative before:content-['*'] before:text-red-500 before:absolute before:top-0 before:right-0 before:translate-x-2">
                  {languageHelper("Gift Card Code")}
                </span>
                <div className="mb-2 pt-0 flex gap-2">
                  <input
                    type="text"
                    name="code"
                    value={formData.code}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        code: e.target.value.toUpperCase(),
                      }))
                    }
                    placeholder="e.g. GC-9X4B-2K1M"
                    className="px-2 py-2 placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full uppercase font-mono font-bold"
                    required
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        code: generateRandomCode(),
                      }))
                    }
                    className="bg-d-cat-btn text-white text-xs px-3 py-2 rounded shadow hover:shadow-lg outline-none focus:outline-none ease-linear transition-all duration-150 flex items-center gap-1 shrink-0"
                    title="Generate a random code"
                  >
                    <FaSyncAlt className="text-xs" />
                    {languageHelper("Generate")}
                  </button>
                </div>
              </label>

              {/* Title */}
              <label htmlFor="" className="col-span-2">
                <span className="text-d-bg-text text-sm mb-2 relative">
                  {languageHelper("Card Title / Label (Optional)")}
                </span>
                <div className="mb-2 pt-0">
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="e.g. Eid Celebration Gift Card, VIP Loyalty Card"
                    className="px-2 py-2 placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full"
                  />
                </div>
              </label>

              {/* Sale Price */}
              <label htmlFor="" className="col-span-2">
                <span className="text-d-bg-text text-sm mb-2 relative before:content-['*'] before:text-red-500 before:absolute before:top-0 before:right-0 before:translate-x-2">
                  {languageHelper("Sale / Purchase Price")} ({currency})
                </span>
                <div className="mb-2 pt-0">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    placeholder="0.00"
                    className="px-2 py-2 placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full"
                    required
                  />
                </div>
              </label>

              {/* Value */}
              <label htmlFor="" className="col-span-2">
                <span className="text-d-bg-text text-sm mb-2 relative before:content-['*'] before:text-red-500 before:absolute before:top-0 before:right-0 before:translate-x-2">
                  {languageHelper("Gift Card Value (Initial Balance)")} ({currency})
                </span>
                <div className="mb-2 pt-0">
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    name="value"
                    value={formData.value}
                    onChange={handleChange}
                    placeholder="100.00"
                    className="px-2 py-2 placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full font-bold text-emerald-700"
                    required
                  />
                </div>
              </label>

              {/* Expiration Date */}
              <label htmlFor="" className="col-span-2">
                <span className="text-d-bg-text text-sm mb-2 relative before:content-['*'] before:text-red-500 before:absolute before:top-0 before:right-0 before:translate-x-2">
                  {languageHelper("Expiration Date")}
                </span>
                <div className="mb-2 pt-0">
                  <input
                    type="date"
                    name="expiration_date"
                    value={formData.expiration_date}
                    onChange={handleChange}
                    className="p-2 placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full"
                    required
                  />
                </div>
              </label>

              {/* Customer Selection */}
              <label htmlFor="" className="col-span-2">
                <span className="text-d-bg-text text-sm mb-2 relative">
                  {languageHelper("Assigned Customer (Optional)")}
                </span>
                <div className="mb-2 pt-0">
                  <Select
                    options={customers}
                    value={selectedCustomerOption}
                    onChange={(option) =>
                      setFormData((prev) => ({
                        ...prev,
                        customer_id: option ? option.value : null,
                      }))
                    }
                    className="placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full"
                    isClearable
                    placeholder={languageHelper("Select a customer or leave for all...")}
                  />
                </div>
              </label>

              {/* Status Radio */}
              <label htmlFor="" className="col-span-2">
                <div className="form-check text-d-bg-text">
                  <div>
                    <h2 className="text-sm mb-2">{languageHelper("Status")}</h2>
                  </div>
                  <input
                    type="radio"
                    name="status"
                    id="status_active"
                    value="active"
                    checked={formData.status === "active"}
                    onChange={handleChange}
                  />
                  <label
                    className="form-check-label pe-5 ps-3 cursor-pointer"
                    htmlFor="status_active"
                  >
                    {languageHelper("Active")}
                  </label>
                  <input
                    className="ps-5"
                    type="radio"
                    name="status"
                    id="status_inactive"
                    value="inactive"
                    checked={formData.status === "inactive"}
                    onChange={handleChange}
                  />
                  <label
                    className="form-check-label ps-3 cursor-pointer"
                    htmlFor="status_inactive"
                  >
                    {languageHelper("Inactive")}
                  </label>
                </div>
              </label>
            </div>

            <span className="w-full bg-d-bg h-1 my-2 inline-block"></span>
            <div className="flex gap-2">
              <button
                className="bg-d-cat-btn text-white text-sm px-3 py-1 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 text-center w-fit relative"
                type="submit"
                disabled={loading}
              >
                {loading && (
                  <span className="absolute top-0 left-0 w-full h-full py-1 px-3 bg-black bg-opacity-50 z-10">
                    <img
                      className="w-4 mx-auto"
                      src="/assets/gif/button_loader.gif"
                      alt=""
                    />
                  </span>
                )}
                {languageHelper("Submit")}
              </button>
              <button
                className="bg-d-cat-btn text-white text-sm px-3 py-1 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 text-center w-fit"
                type="button"
                onClick={() => navigate(-1)}
              >
                {languageHelper("Back")}
              </button>
            </div>
          </form>
        </div>
      </div>
    </main>
  );
}

export default AddGiftCardForm;
